import {
  DynamicLink,
  PortalServiceBlockDto,
  PortalServiceBlockDtoEmbedType,
  PortalServiceBlockDtoType,
  PortalServiceChatBlockDto,
  PortalServiceContentBlockDto,
  PortalServiceDocsBlockDto,
  PortalServiceEmbedBlockDto,
  PortalServiceFilesBucketBlockDto,
  PortalServiceFilesDashboardBlockDto,
  PortalServiceFilesRecentBlockDto,
  PortalServiceFlexibleBlockDto,
  PortalServiceHelpBlockDto,
  PortalServiceInfoBlockDto,
  PortalServiceLinksBlockDto,
  PortalServiceMeetingsBlockDto,
  PortalServiceNoteBlockDto,
  PortalServiceProjectionBlockDto,
  PortalServiceRequestBlockDto,
  PortalServiceTaskBlockDto,
} from '@nimbus/portal-service';
import { PortalBlockData } from './portal-block-data';
import { FlexibleBlockBrick, FlexibleBlockProps, FlexibleBlockTemplate } from './portal-block-flexible';
import { PortalBgType, PortalGradientColor, PortalSolidColor, PortalTextType } from './portal-colors';
import { PortalStyleConfig } from './portal-theme';
import { TailwindClassName } from './tailwind-class-name';
import { ThemeColorSchemas } from './theme-color-schemas';

export type PortalBlockPropsBase = {
  index: number;
  themeColors: ThemeColorSchemas;
  themeStyle: PortalStyleConfig;
  onLoadSuccess?: () => void;
  orgDomain: string;
};

export enum PortalBlockType {
  EMBED = 'embed',
  TASK_LIST = 'task-list',
  CHAT = 'chat',
  NOTE = 'note',
  CONTENT = 'content',
  INFO = 'info',
  DOCS = 'docs',
  HELP = 'help',
  REQUEST = 'request',
  MEETINGS = 'meetings',
  LINKS = 'links',
  CHAT_NO_ACCESS = 'chat-no-access',
  TASK_LIST_NO_ACCESS = 'task-list-no-access',
  FILES_DASHBOARD = 'files-dashboard',
  FILES_RECENT = 'files-recent',
  FILES_BUCKET = 'files-bucket',
  FLEXIBLE = 'flexible',
  TASKS_DASHBOARD = 'tasks-dashboard',
  CHATS_DASHBOARD = 'chats-dashboard',
  PROJECTION = 'projection',
}

export enum PortalBlockEmbedType {
  CUSTOM = 'custom',
  YOUTUBE = 'youtube',
  GOOGLE_DRIVE = 'google-drive',
  MS_DOC = 'ms-doc',
  CALENDLY = 'calendly',
  AIRTABLE = 'airtable',
  LOOM = 'loom',
  DATA_STUDIO = 'data-studio',
  FIGMA = 'figma',
  MIRO = 'miro',
  UNKNOWN = 'unknown',
  HUBSPOT = 'hubspot',
}

export interface BlockFilesQuery {
  conjunction: 'or' | 'and';
  bucketIds?: string[];
  targets?: string[];
  targetIds?: string[];
  userIds?: number[];
  types?: string[];
}

export interface BlockFilesSort {
  field: 'createdAt' | 'filename' | 'size';
  order: 'asc' | 'desc';
}

export interface BlockFilesLimit {
  size: number;
  from: number;
}

export interface BlockFilesSearch {
  query: BlockFilesQuery;
  sort: BlockFilesSort;
  limit: BlockFilesLimit;
}

export interface BlockTasksQuery {
  conjunction: 'or' | 'and';
  userIds?: number[];
}

export interface BlockTasksSort {
  field: 'createdAt';
  order: 'asc' | 'desc';
}

export interface BlockTasksLimit {
  size: number;
  from: number;
}

export interface BlockTasksSearch {
  query: BlockTasksQuery;
  sort: BlockTasksSort;
  limit: BlockTasksLimit;
}

export type PortalBlockPlace =
  | 'global_body'
  | 'global_header'
  | 'global_footer'
  | 'page_body_top'
  | 'page_body'
  | 'page_body_bottom'
  | 'page_header'
  | 'page_footer';

export type BlockActionType = 'editContentButton';

export interface BlockActionBase {
  type: BlockActionType;
  enabled: boolean;
}

export interface PortalServiceBlockActionEditContentButton extends BlockActionBase {
  type: 'editContentButton';
  title: string;
}

export type BlockWrapperType = 'blank' | 'filled' | 'none' | 'bare' | 'custom';
export interface BlockWrapperInterface {
  type: BlockWrapperType;
  showTitle: boolean;
  className: TailwindClassName[];
  theme: {
    text: Record<string, PortalTextType | PortalSolidColor>;
    bg: Record<string, PortalBgType | PortalGradientColor>;
  };
}

export type BlockMediaSize = 'base' | 'md' | 'lg';

export type BlockAction = PortalServiceBlockActionEditContentButton;

export interface PortalBaseBlockUnpersisted {
  ghost?: boolean;
  ghostId?: string;
  external?: boolean;
  projection?: Omit<ProjectionBlock, 'block'>;
}

export interface PortalBaseBlock extends PortalBaseBlockUnpersisted {
  id: string;
  title: string;
  index: number;
  place: PortalBlockPlace;
  colspan: number;
  rowspan: number;
  actions: BlockAction[];
  wrapper: BlockWrapperInterface;
  mediaSizes: BlockMediaSize[];
  enabled: boolean;
  pageId: string;
  removable: boolean;
  data: PortalBlockData;
  originGlobalId?: string | null;
  isOrigin?: boolean | null;
}

export interface InfoBlock extends PortalBaseBlock {
  type: PortalBlockType.INFO;
  image: string;
  link: string;
  openInNewTab: boolean;
}

export interface EmbedBlock extends PortalBaseBlock {
  type: PortalBlockType.EMBED;
  embedType: PortalBlockEmbedType;
  content: string;
  contentUrl: string;
}

export type PortalNoteTemplate =
  | 'board'
  | 'task-list'
  | 'faq'
  | 'flow-guides'
  | 'onboarding'
  | 'welcome-page'
  | 'blank-page';

export interface NoteBlock extends PortalBaseBlock {
  type: PortalBlockType.NOTE;
  orgId: string;
  workspaceId: string;
  noteId: string;
  shareId?: number;
  shareSecurityKey?: string;
  templateKey?: PortalNoteTemplate;
}

export interface ContentBlock extends Omit<NoteBlock, 'type'> {
  type: PortalBlockType.CONTENT;
  title: string;
  text: string;
}

export interface DocsBlock extends PortalBaseBlock {
  type: PortalBlockType.DOCS;
  image: string;
  link: string;
  openInNewTab: boolean;
}

export interface RequestBlock extends PortalBaseBlock {
  type: PortalBlockType.REQUEST;
  image: string;
  link: string;
  openInNewTab: boolean;
}

export interface HelpBlock extends PortalBaseBlock {
  type: PortalBlockType.HELP;
  image: string;
  link: string;
  openInNewTab: boolean;
}

export interface MeetingsBlock extends PortalBaseBlock {
  type: PortalBlockType.MEETINGS;
  image: string;
  link: string;
  openInNewTab: boolean;
}

export interface LinksBlock extends PortalBaseBlock {
  type: PortalBlockType.LINKS;
  image: string;
  link: string;
  openInNewTab: boolean;
}

export interface TaskBlock extends PortalBaseBlock {
  type: PortalBlockType.TASK_LIST;
  orgId: string;
  workspaceId: string;
  taskListIds: string[];
  readonlyEnabled: boolean;
}

export interface TaskBlockNoAccess extends PortalBaseBlock {
  type: PortalBlockType.TASK_LIST_NO_ACCESS;
}

export interface ChatBlock extends PortalBaseBlock {
  type: PortalBlockType.CHAT;
  orgId: string;
  workspaceId: string;
  readonlyEnabled: boolean;
  updatedAt?: number;
}

export interface ChatBlockNoAccess extends PortalBaseBlock {
  type: PortalBlockType.CHAT_NO_ACCESS;
}

export interface FilesDashboardBlock
  extends PortalBaseBlock,
    Pick<BlockFilesSearch, 'sort'>,
    Partial<Pick<BlockFilesSearch, 'query' | 'limit'>> {
  type: PortalBlockType.FILES_DASHBOARD;
}

export interface FilesRecentBlock
  extends PortalBaseBlock,
    Pick<BlockFilesSearch, 'sort' | 'limit'>,
    Partial<Pick<BlockFilesSearch, 'query'>> {
  type: PortalBlockType.FILES_RECENT;
  dynamicLink?: DynamicLink[];
}

export interface FilesBucketBlock extends PortalBaseBlock, Partial<Pick<BlockFilesSearch, 'sort' | 'limit'>> {
  type: PortalBlockType.FILES_BUCKET;
  description: string;
  query: Required<Pick<BlockFilesSearch['query'], 'bucketIds'>> & Omit<BlockFilesSearch['query'], 'bucketIds'>;
}

export interface TasksDashboardBlock extends PortalBaseBlock {
  type: PortalBlockType.TASKS_DASHBOARD;
}

export interface FlexibleBlock extends PortalBaseBlock, FlexibleBlockProps {
  type: PortalBlockType.FLEXIBLE;
}

export type FlexibleBlockModifierBrick = Pick<FlexibleBlockBrick, 'brickType'> & Partial<FlexibleBlockBrick>;

export type FlexibleBlockModifier = Partial<Omit<FlexibleBlock, 'bricks'>> & {
  bricks?: Array<FlexibleBlockModifierBrick>;
};

export interface ProjectionBlock extends PortalBaseBlock {
  type: PortalBlockType.PROJECTION;
  sourcePortalId: string;
  sourcePageId: string;
  sourceBlockId: string;
  block?: PortalBlock;
}

export interface ChatsDashboardBlock extends PortalBaseBlock {
  type: PortalBlockType.CHATS_DASHBOARD;
}

export type PortalPersistentBlock =
  | InfoBlock
  | EmbedBlock
  | NoteBlock
  | ContentBlock
  | DocsBlock
  | RequestBlock
  | HelpBlock
  | MeetingsBlock
  | LinksBlock
  | TaskBlock
  | ChatBlock
  | FilesDashboardBlock
  | FilesRecentBlock
  | FilesBucketBlock
  | FlexibleBlock
  | TasksDashboardBlock
  | ChatsDashboardBlock
  | ProjectionBlock;

export type PortalBlock = PortalPersistentBlock | TaskBlockNoAccess | ChatBlockNoAccess;

export const isPersistantBlockType = (block?: PortalBlock): block is PortalPersistentBlock => {
  if (block == null) {
    return false;
  }
  return block.type !== PortalBlockType.TASK_LIST_NO_ACCESS && block.type !== PortalBlockType.CHAT_NO_ACCESS;
};

export const isProjectionBlockType = (block?: PortalBlock): block is ProjectionBlock => {
  if (block == null) {
    return false;
  }
  return block.type === PortalBlockType.PROJECTION;
};

export const isRichTextBlockType = (block?: PortalBlock): block is NoteBlock | ContentBlock => {
  if (block == null) {
    return false;
  }
  return block.type === PortalBlockType.NOTE || block.type === PortalBlockType.CONTENT;
};

export type ChangeButtonType = NoteBlock | ContentBlock;
export type BlockIndexData = Pick<PortalBlock, 'id' | 'place' | 'index'>;
export type BlockPlaceData = Pick<PortalBlock, 'id' | 'place' | 'index'>;
export type BlockSizeData = Pick<PortalBlock, 'id' | 'colspan' | 'rowspan' | 'mediaSizes'>;
export type BlockVisibilityData = Pick<PortalBlock, 'id' | 'enabled'>;
export type BlockDataData = Pick<PortalBlock, 'id' | 'data'>;
export type BlockUpdateData = Omit<
  Exclude<PortalBlock, ChatBlockNoAccess | TaskBlockNoAccess>,
  'type' | 'place' | 'index' | 'actions' | 'colspan' | 'rowspan' | 'mediaSizes' | 'enabled' | 'removable'
>;
export type BlockActionsData = Pick<PortalBlock, 'id' | 'actions'>;

// Transformers
const portalBlockEmbedTypeConverter: Record<PortalServiceBlockDtoEmbedType, PortalBlockEmbedType> = {
  'custom': PortalBlockEmbedType.CUSTOM,
  'youtube': PortalBlockEmbedType.YOUTUBE,
  'google-drive': PortalBlockEmbedType.GOOGLE_DRIVE,
  'ms-doc': PortalBlockEmbedType.MS_DOC,
  'calendly': PortalBlockEmbedType.CALENDLY,
  'airtable': PortalBlockEmbedType.AIRTABLE,
  'loom': PortalBlockEmbedType.LOOM,
  'data-studio': PortalBlockEmbedType.DATA_STUDIO,
  'figma': PortalBlockEmbedType.FIGMA,
  'miro': PortalBlockEmbedType.MIRO,
  'unknown': PortalBlockEmbedType.UNKNOWN,
  'hubspot': PortalBlockEmbedType.HUBSPOT,
};

const portalBlockBaseFromDto = ({
  id,
  title,
  index,
  place,
  colspan,
  rowspan,
  actions,
  wrapper,
  mediaSizes,
  enabled = true,
  removable = true,
  pageId,
  data,
  originGlobalId,
  isOrigin,
}: PortalServiceBlockDto): PortalBaseBlock => {
  return {
    id,
    title,
    index,
    place,
    colspan,
    rowspan,
    actions,
    mediaSizes,
    enabled,
    removable,
    pageId,
    wrapper: wrapper as BlockWrapperInterface,
    data: (data as PortalBlockData) ?? [],
    originGlobalId,
    isOrigin,
  };
};

const portalBlockFromDtoFnsMap: Record<PortalServiceBlockDtoType, (dto: PortalServiceBlockDto) => PortalBlock> = {
  [PortalBlockType.EMBED]: (dto) => {
    const { embedType, content, contentUrl } = <PortalServiceEmbedBlockDto>dto;
    return {
      ...portalBlockBaseFromDto(dto),
      type: PortalBlockType.EMBED,
      embedType: portalBlockEmbedTypeConverter[embedType],
      content,
      contentUrl,
    };
  },
  [PortalBlockType.TASK_LIST]: (dto) => {
    const { orgId, workspaceId, taskListIds, readonlyEnabled } = <PortalServiceTaskBlockDto>dto;
    return {
      ...portalBlockBaseFromDto(dto),
      type: PortalBlockType.TASK_LIST,
      orgId,
      workspaceId,
      taskListIds,
      readonlyEnabled,
    };
  },
  [PortalBlockType.CHAT]: (dto) => {
    const { orgId, workspaceId, readonlyEnabled } = <PortalServiceChatBlockDto>dto;
    return {
      ...portalBlockBaseFromDto(dto),
      type: PortalBlockType.CHAT,
      orgId,
      workspaceId,
      readonlyEnabled,
    };
  },
  [PortalBlockType.NOTE]: (dto) => {
    const { orgId, workspaceId, noteId, shareId, shareSecurityKey } = <PortalServiceNoteBlockDto>dto;
    return {
      ...portalBlockBaseFromDto(dto),
      type: PortalBlockType.NOTE,
      orgId,
      workspaceId,
      noteId,
      shareId,
      shareSecurityKey,
    };
  },
  [PortalBlockType.CONTENT]: (dto) => {
    const { orgId, workspaceId, noteId, shareId, shareSecurityKey, text } = <PortalServiceContentBlockDto>dto;
    return {
      ...portalBlockBaseFromDto(dto),
      type: PortalBlockType.CONTENT,
      orgId,
      workspaceId,
      noteId,
      shareId,
      shareSecurityKey,
      text,
    };
  },
  [PortalBlockType.INFO]: (dto) => {
    const { openInNewTab, link, image } = <PortalServiceInfoBlockDto>dto;
    return {
      ...portalBlockBaseFromDto(dto),
      type: PortalBlockType.INFO,
      image,
      link,
      openInNewTab,
    };
  },
  [PortalBlockType.DOCS]: (dto) => {
    const { openInNewTab, link, image } = <PortalServiceDocsBlockDto>dto;
    return {
      ...portalBlockBaseFromDto(dto),
      type: PortalBlockType.DOCS,
      image,
      link,
      openInNewTab,
    };
  },
  [PortalBlockType.HELP]: (dto) => {
    const { openInNewTab, link, image } = <PortalServiceHelpBlockDto>dto;
    return {
      ...portalBlockBaseFromDto(dto),
      type: PortalBlockType.HELP,
      image,
      link,
      openInNewTab,
    };
  },
  [PortalBlockType.REQUEST]: (dto) => {
    const { openInNewTab, link, image } = <PortalServiceRequestBlockDto>dto;
    return {
      ...portalBlockBaseFromDto(dto),
      type: PortalBlockType.REQUEST,
      image,
      link,
      openInNewTab,
    };
  },
  [PortalBlockType.MEETINGS]: (dto) => {
    const { openInNewTab, link, image } = <PortalServiceMeetingsBlockDto>dto;
    return {
      ...portalBlockBaseFromDto(dto),
      type: PortalBlockType.MEETINGS,
      image,
      link,
      openInNewTab,
    };
  },
  [PortalBlockType.LINKS]: (dto) => {
    const { openInNewTab, link, image } = <PortalServiceLinksBlockDto>dto;
    return {
      ...portalBlockBaseFromDto(dto),
      type: PortalBlockType.LINKS,
      image,
      link,
      openInNewTab,
    };
  },
  [PortalBlockType.FILES_DASHBOARD]: (dto) => {
    const { query, sort, limit } = <PortalServiceFilesDashboardBlockDto>dto;
    return {
      ...portalBlockBaseFromDto(dto),
      type: PortalBlockType.FILES_DASHBOARD,
      query,
      sort,
      limit,
      removable: false,
    };
  },
  [PortalBlockType.FILES_RECENT]: (dto) => {
    const { query, sort, limit, dynamicLink } = <PortalServiceFilesRecentBlockDto>dto;
    return {
      ...portalBlockBaseFromDto(dto),
      type: PortalBlockType.FILES_RECENT,
      query,
      sort,
      limit,
      dynamicLink,
    };
  },
  [PortalBlockType.FILES_BUCKET]: (dto) => {
    const { query, sort, limit, description } = <PortalServiceFilesBucketBlockDto>dto;
    return {
      ...portalBlockBaseFromDto(dto),
      type: PortalBlockType.FILES_BUCKET,
      query,
      sort,
      limit,
      description,
    };
  },
  [PortalBlockType.TASKS_DASHBOARD]: (dto) => {
    return {
      ...portalBlockBaseFromDto(dto),
      type: PortalBlockType.TASKS_DASHBOARD,
      removable: false,
    };
  },
  [PortalBlockType.FLEXIBLE]: (dto) => {
    const { template, className, bricks } = <PortalServiceFlexibleBlockDto>dto;
    return {
      ...portalBlockBaseFromDto(dto),
      type: PortalBlockType.FLEXIBLE,
      className: className as TailwindClassName[],
      bricks: bricks as unknown as FlexibleBlockBrick[],
      template: (typeof template === 'string' ? { type: template, view: 'basic' } : template) as FlexibleBlockTemplate,
    };
  },
  [PortalBlockType.CHATS_DASHBOARD]: (dto) => {
    return {
      ...portalBlockBaseFromDto(dto),
      type: PortalBlockType.CHATS_DASHBOARD,
      removable: false,
    };
  },
  [PortalBlockType.PROJECTION]: (dto) => {
    const { sourcePortalId, sourcePageId, sourceBlockId } = <PortalServiceProjectionBlockDto>dto;
    return {
      ...portalBlockBaseFromDto(dto),
      type: PortalBlockType.PROJECTION,
      sourcePortalId,
      sourcePageId,
      sourceBlockId,
    };
  },
};

export function portalBlockFromDto(dto: PortalServiceBlockDto): PortalBlock {
  return portalBlockFromDtoFnsMap[dto.type](dto);
}
